'use client';

/**
 * Reusable library catalog card.
 *
 * Module: components/library/BookCard
 * Layer:  component
 *
 * Purpose:
 *     Renders one {@link LibraryBook} as a poster card: cover (with a glyph
 *     fallback), title, authors, a color-coded type badge, an availability
 *     indicator, and — for available books when a checkout handler is
 *     supplied — a checkout button. Sub-components are split so every
 *     function clears the CC<=5 / <=60-line gates.
 *
 * AI context:
 *     - Entry points called by: src/app/library/book-grid (the catalog grid).
 *     - Depends on: next/image, next/link, lib/library (types + token tables),
 *       lib/i18n.
 *     - Side effects: none directly; the checkout button delegates to a
 *       caller-owned async handler.
 *     - Invariants: presentational; never fetches. Availability is derived
 *       purely from the book's access model + copy counts.
 *     - Replacement cost: LOW.
 *
 * Threat model:
 *     - `cover_url` is backend-controlled and passed to next/image, which only
 *       optimizes configured remote hosts. All text is React-escaped.
 */

import type { Route } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  BOOK_TYPE_BADGE_CLASS,
  BOOK_TYPE_LABEL_KEY,
  type LibraryBook,
} from '@/lib/library';

/** Props for {@link BookCard}. */
export interface BookCardProps {
  readonly book: LibraryBook;
  /** Whether the signed-in visitor may check books out. */
  readonly canCheckout: boolean;
  /** True while a checkout request for this book is in flight. */
  readonly isCheckingOut: boolean;
  /** Invoked with the book id when the checkout button is pressed. */
  readonly onCheckout: (bookId: number) => void;
}

/** Build the typed detail route for a book id. */
function bookHref(id: number): Route {
  // WHY: typedRoutes types this template as Route at the call site; the cast
  // documents that the id is an app-owned, URL-safe numeric segment.
  return `/library/${String(id)}` as Route;
}

/** True when the book can be checked out right now (a copy is free). */
function isAvailable(book: LibraryBook): boolean {
  return book.access_model === 'unlimited' || book.available_copies > 0;
}

/** Book cover with a glyph placeholder fallback. */
function Cover(props: { readonly book: LibraryBook }): React.JSX.Element {
  const { t } = useTranslation();
  const { book } = props;
  if (book.cover_url) {
    return (
      <Image
        src={book.cover_url}
        alt={t('library.book.coverAlt', { title: book.title })}
        fill
        sizes='(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw'
        className='object-cover'
      />
    );
  }
  return (
    <div className='flex size-full items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200'>
      <span className='text-4xl text-blue-400' aria-hidden='true'>
        &#128214;
      </span>
    </div>
  );
}

/** Color-coded media-type badge. */
function TypeBadge(props: { readonly book: LibraryBook }): React.JSX.Element {
  const { t } = useTranslation();
  const { book } = props;
  return (
    <span
      className={`absolute left-3 top-3 rounded px-2 py-1 text-xs font-semibold ${
        BOOK_TYPE_BADGE_CLASS[book.book_type]
      }`}
    >
      {t(BOOK_TYPE_LABEL_KEY[book.book_type])}
    </span>
  );
}

/** Authors line, falling back to an "unknown author" label. */
function Authors(props: { readonly book: LibraryBook }): React.JSX.Element {
  const { t } = useTranslation();
  const { authors } = props.book;
  const label =
    authors.length > 0
      ? t('library.book.byAuthors', { authors: authors.join(', ') })
      : t('library.book.unknownAuthor');
  return <p className='mt-1 line-clamp-1 text-sm opacity-70'>{label}</p>;
}

/** Availability indicator derived from access model + copy counts. */
function Availability(props: { readonly book: LibraryBook }): React.JSX.Element {
  const { t } = useTranslation();
  const { book } = props;
  if (book.access_model === 'unlimited') {
    return <span className='text-sm font-medium text-green-600'>{t('library.availability.unlimited')}</span>;
  }
  if (book.available_copies > 0) {
    return (
      <span className='text-sm font-medium text-green-600'>
        {t('library.availability.available', { count: book.available_copies })}
      </span>
    );
  }
  return <span className='text-sm font-medium text-red-600'>{t('library.availability.checkedOut')}</span>;
}

/** Checkout button shown only when available and the visitor may check out. */
function CheckoutButton(props: { readonly book: BookCardProps }): React.JSX.Element | null {
  const { t } = useTranslation();
  const { book, canCheckout, isCheckingOut, onCheckout } = props.book;
  const handleCheckout = useCallback((): void => {
    onCheckout(book.id);
  }, [onCheckout, book.id]);

  if (!canCheckout || !isAvailable(book)) {
    return null;
  }
  return (
    <button
      type='button'
      onClick={handleCheckout}
      disabled={isCheckingOut}
      className='mt-3 w-full rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60'
    >
      {isCheckingOut ? t('library.book.checkingOut') : t('library.book.checkout')}
    </button>
  );
}

/**
 * Poster card for a single library book.
 *
 * @param props - The book plus checkout affordance state and handler.
 * @returns The card element linking to the book's detail view.
 * @remarks The checkout button stops link navigation; the rest of the card is
 *   a link to `/library/{id}`.
 */
export function BookCard(props: BookCardProps): React.JSX.Element {
  const { t } = useTranslation();
  const { book } = props;
  return (
    <div className='group flex flex-col overflow-hidden rounded-xl border bg-white transition-shadow hover:shadow-lg'>
      <Link href={bookHref(book.id)} className='relative block aspect-[3/4] bg-gray-100'>
        <Cover book={book} />
        <TypeBadge book={book} />
      </Link>
      <div className='flex flex-1 flex-col p-4'>
        <Link href={bookHref(book.id)} className='flex-1'>
          <h3 className='line-clamp-2 font-semibold transition-colors group-hover:text-blue-600'>
            {book.title}
          </h3>
          <Authors book={book} />
        </Link>
        <div className='mt-3 border-t pt-3'>
          <Availability book={book} />
          <Link
            href={bookHref(book.id)}
            className='mt-2 block text-sm text-blue-600 hover:underline'
          >
            {t('library.book.viewDetails')}
          </Link>
          <CheckoutButton book={props} />
        </div>
      </div>
    </div>
  );
}
